import {
    type ReactNode,
    useState,
    useMemo
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    EmbeddedMenu,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    PlusIcon,
    HomeIcon,
    FileIcon,
    UserIcon,
    BellIcon
} from "../../anatomy/icons";

const NAME = "EmbeddedMenu";

const USAGE_CODE = `<View
    style={{
        flexDirection: "row",
        flex: 1
    }}
>
    <EmbeddedMenu
        buttons={[
            {
                redirectMain: "Home",
                icon: HomeIcon,
                title: "Home"
            },
            {
                redirectMain: "Library",
                icon: FileIcon,
                title: "Library",
                badge: 3
            }
        ]}
        siteLogoProps={{
            subTitle: "Design System",
            title: "NCore"
        }}
        navigation={navigation}
        id="app-menu"
        isCollapse={true}
    />
    <PageContent/>
</View>`;

const FOOTER_CODE = `<EmbeddedMenu
    renderFooter={({ isCollapse }) => <Button
        title={isCollapse ? "New item" : undefined}
        accessibilityLabel="New item"
        spreadBehaviour="stretch"
        variant="outline"
        icon={PlusIcon}
        type="neutral"
    />}
    ...
/>`;

const INITIAL_ROUTE = "home";

const stylesheet = StyleSheet.create({
    content: {
        justifyContent: "center",
        alignItems: "center",
        minWidth: 0,
        flex: 1
    },
    stage: {
        flexDirection: "row",
        height: "100%",
        width: "100%"
    }
});

const MENU_PROPS = {
    isWorkWithSafeAreaView: false,
    isWorkWithHeaderSpace: false
} as const;

const createDemoNavigation = (route: string, onNavigate: (route: string) => void): NCoreUIKit.Navigation => {
    const state: NCoreUIKit.NavigationState = {
        routes: [
            {
                name: route,
                key: route
            }
        ],
        index: 0
    };

    return {
        navigate: (page, config) => onNavigate(config?.screen ?? page),
        addListener: () => () => undefined,
        getRootState: () => state,
        goBack: () => undefined,
        canGoBack: () => false,
        getState: () => state
    };
};

const Stage = ({
    children,
    title
}: {
    children: ReactNode;
    title: string;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <DemoFrame
        height={shapeConfigs.chartHeight.large}
    >
        <View
            style={stylesheet.stage}
        >
            {children}
            <View
                style={[
                    stylesheet.content,
                    {
                        padding: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {title}
                </Text>
            </View>
        </View>
    </DemoFrame>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("embeddedMenuPage.sidebarTitle"),
        id: "built-from-sidebar"
    },
    {
        title: t("embeddedMenuPage.modesTitle"),
        id: "modes"
    },
    {
        title: t("embeddedMenuPage.itemsTitle"),
        id: "items"
    },
    {
        title: t("embeddedMenuPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const EmbeddedMenuPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        route,
        setRoute
    ] = useState(INITIAL_ROUTE);

    const [
        isExpanded,
        setIsExpanded
    ] = useState(true);

    const [
        isAutoClosed,
        setIsAutoClosed
    ] = useState(false);

    const [
        lastEvent,
        setLastEvent
    ] = useState("—");

    const navigation = useMemo(() => createDemoNavigation(route, setRoute), [
        route
    ]);

    const routeTitles: Record<string, string> = {
        notifications: t("embeddedMenuPage.notifications"),
        favourites: t("embeddedMenuPage.favourites"),
        documents: t("embeddedMenuPage.documents"),
        library: t("sample.library"),
        profile: t("sample.item1"),
        home: t("sample.home")
    };

    const currentTitle = routeTitles[route] ?? route;

    const handleExpandedOpen = () => {
        setIsExpanded(true);

        setLastEvent("onOpen");
    };

    const handleExpandedClose = () => {
        setIsExpanded(false);

        setLastEvent("onClose");
    };

    const renderFooter = ({
        isCollapse
    }: {
        isCollapse: boolean;
    }) => <Button
        title={isCollapse ? t("embeddedMenuPage.newItem") : undefined}
        accessibilityLabel={t("embeddedMenuPage.newItem")}
        spreadBehaviour="stretch"
        onPress={() => undefined}
        variant="outline"
        icon={PlusIcon}
        type="neutral"
    />;

    return <Article>
        <ComponentHeader
            lead={t("embeddedMenuPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("embeddedMenuPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("embeddedMenuPage.overviewCaption", [
                    currentTitle
                ])}
            >
                <Stage
                    title={currentTitle}
                >
                    <EmbeddedMenu
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon,
                                badge: 3
                            },
                            {
                                title: t("sample.item1"),
                                redirectMain: "profile",
                                icon: UserIcon
                            }
                        ]}
                        siteLogoProps={{
                            subTitle: t("sample.subtitle"),
                            title: t("sample.brand")
                        }}
                        id="docs-embedded-menu-overview"
                        navigation={navigation}
                        {...MENU_PROPS}
                        isCollapse={true}
                    />
                </Stage>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("embeddedMenuPage.sidebarTitle")}
            id="built-from-sidebar"
        >
            <Paragraph>
                {t("embeddedMenuPage.sidebarText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("embeddedMenuPage.sidebarChoice")}
            </Paragraph>
        </Section>
        <Section
            title={t("embeddedMenuPage.modesTitle")}
            id="modes"
        >
            <Paragraph>
                {t("embeddedMenuPage.modesText")}
            </Paragraph>
            <Preview
                caption={t("embeddedMenuPage.modesCaption", [
                    lastEvent
                ])}
            >
                <Switch
                    onCheckedChange={setIsExpanded}
                    isChecked={isExpanded}
                    title="isCollapse"
                />
                <Switch
                    onCheckedChange={setIsAutoClosed}
                    isChecked={isAutoClosed}
                    title="isAutoClosed"
                />
                <Stage
                    title={currentTitle}
                >
                    <EmbeddedMenu
                        onOpened={() => setLastEvent("onOpened")}
                        onClosed={() => setLastEvent("onClosed")}
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon
                            },
                            {
                                title: t("sample.item1"),
                                redirectMain: "profile",
                                icon: UserIcon
                            }
                        ]}
                        siteLogoProps={{
                            subTitle: t("sample.subtitle"),
                            title: t("sample.brand")
                        }}
                        id="docs-embedded-menu-modes"
                        onClose={handleExpandedClose}
                        onOpen={handleExpandedOpen}
                        isAutoClosed={isAutoClosed}
                        isCollapse={isExpanded}
                        navigation={navigation}
                        {...MENU_PROPS}
                    />
                </Stage>
            </Preview>
        </Section>
        <Section
            title={t("embeddedMenuPage.itemsTitle")}
            id="items"
        >
            <Paragraph>
                {t("embeddedMenuPage.itemsText")}
            </Paragraph>
            <Preview>
                <Stage
                    title={currentTitle}
                >
                    <EmbeddedMenu
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                subButtons: [
                                    {
                                        title: t("embeddedMenuPage.documents"),
                                        redirectMain: "library",
                                        redirectSub: "documents"
                                    },
                                    {
                                        title: t("embeddedMenuPage.favourites"),
                                        redirectSub: "favourites",
                                        redirectMain: "library"
                                    }
                                ],
                                title: t("sample.library"),
                                isCollapsible: true,
                                isCollapse: true,
                                icon: FileIcon,
                                badge: 5
                            },
                            {
                                title: t("embeddedMenuPage.notifications"),
                                redirectMain: "notifications",
                                icon: BellIcon,
                                badge: 8
                            }
                        ]}
                        siteLogoProps={{
                            subTitle: t("sample.subtitle"),
                            title: t("sample.brand")
                        }}
                        id="docs-embedded-menu-items"
                        navigation={navigation}
                        {...MENU_PROPS}
                        isCollapse={true}
                    />
                </Stage>
            </Preview>
        </Section>
        <Section
            title={t("embeddedMenuPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("embeddedMenuPage.layoutText")}
            </Paragraph>
            <Preview>
                <Stage
                    title={currentTitle}
                >
                    <EmbeddedMenu
                        buttons={[
                            {
                                title: t("sample.home"),
                                redirectMain: "home",
                                icon: HomeIcon
                            },
                            {
                                title: t("sample.library"),
                                redirectMain: "library",
                                icon: FileIcon
                            }
                        ]}
                        siteLogoProps={{
                            subTitle: t("sample.subtitle"),
                            title: t("sample.brand")
                        }}
                        id="docs-embedded-menu-layout"
                        renderFooter={renderFooter}
                        navigation={navigation}
                        {...MENU_PROPS}
                        isCollapse={true}
                    />
                </Stage>
            </Preview>
            <CodeBlock
                code={FOOTER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("embeddedMenuPage.a11yLandmark"),
                    t("embeddedMenuPage.a11yRows"),
                    t("embeddedMenuPage.a11yRail"),
                    t("embeddedMenuPage.a11yToggle"),
                    t("embeddedMenuPage.a11yFlyout"),
                    t("embeddedMenuPage.a11yFlyoutNested"),
                    t("embeddedMenuPage.a11yMotion"),
                    t("embeddedMenuPage.a11yDirection"),
                    t("embeddedMenuPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("embeddedMenuPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("embeddedMenuPage.propButtons"),
                        type: "Array<EmbeddedMenuButton>",
                        name: "buttons"
                    },
                    {
                        description: t("embeddedMenuPage.propNavigation"),
                        type: "NCoreUIKit.Navigation",
                        name: "navigation"
                    },
                    {
                        description: t("embeddedMenuPage.propIsCollapse"),
                        defaultValue: "false",
                        name: "isCollapse",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propIsAutoClosed"),
                        name: "isAutoClosed",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propIsAutoClosedOnClosed"),
                        name: "isAutoClosedOnClosed",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propIsShowCollapseButton"),
                        name: "isShowCollapseButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propIsShowSiteLogo"),
                        name: "isShowSiteLogo",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propSiteLogoProps"),
                        type: "ISiteLogoProps",
                        name: "siteLogoProps"
                    },
                    {
                        description: t("embeddedMenuPage.propRenderSiteLogo"),
                        type: "(props: { isActive: boolean }) => ReactNode",
                        name: "renderSiteLogo"
                    },
                    {
                        type: "(props: { isCollapse, maxWidth, minWidth }) => ReactNode",
                        description: t("embeddedMenuPage.propRenderHeader"),
                        name: "renderHeader"
                    },
                    {
                        description: t("embeddedMenuPage.propRenderHeaderSeparator"),
                        name: "renderHeaderSeparator",
                        type: "() => ReactNode"
                    },
                    {
                        type: "(props: { isCollapse, maxWidth, minWidth }) => ReactNode",
                        description: t("embeddedMenuPage.propRenderFooter"),
                        name: "renderFooter"
                    },
                    {
                        description: t("embeddedMenuPage.propMaxWidth"),
                        name: "maxWidth",
                        type: "number"
                    },
                    {
                        description: t("embeddedMenuPage.propMinWidth"),
                        name: "minWidth",
                        type: "number"
                    },
                    {
                        description: t("embeddedMenuPage.propBackgroundColor"),
                        type: "keyof ProjectColorPalette",
                        name: "backgroundColor"
                    },
                    {
                        description: t("embeddedMenuPage.propIsWorkWithSafeAreaView"),
                        name: "isWorkWithSafeAreaView",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propIsWorkWithHeaderSpace"),
                        name: "isWorkWithHeaderSpace",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propHeaderSpace"),
                        name: "headerSpace",
                        type: "number"
                    },
                    {
                        description: t("embeddedMenuPage.propIsWorkWithAnimation"),
                        name: "isWorkWithAnimation",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propClose"),
                        type: "() => void",
                        name: "close"
                    },
                    {
                        description: t("embeddedMenuPage.propOnOpen"),
                        type: "(props: { id?: string }) => void",
                        name: "onOpen"
                    },
                    {
                        description: t("embeddedMenuPage.propOnOpened"),
                        type: "(props: { id?: string }) => void",
                        name: "onOpened"
                    },
                    {
                        description: t("embeddedMenuPage.propOnClose"),
                        type: "(props: { id?: string }) => void",
                        name: "onClose"
                    },
                    {
                        description: t("embeddedMenuPage.propOnClosed"),
                        type: "(props: { id?: string }) => void",
                        name: "onClosed"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("embeddedMenuPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("embeddedMenuPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("embeddedMenuPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("embeddedMenuPage.reservedText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("embeddedMenuPage.propIsCollapsible"),
                        name: "isCollapsible",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propLogo"),
                        type: "{ image?, imageUrl?, title? }",
                        name: "logo"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    buttons
                </InlineCode>
                {" "}
                {t("embeddedMenuPage.partButtonText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("embeddedMenuPage.propButtonTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonIsUseLocalize"),
                        name: "isUseLocalize",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonRedirectMain"),
                        name: "redirectMain",
                        type: "string"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonRedirectSub"),
                        name: "redirectSub",
                        type: "string"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonIsCollapsible"),
                        name: "isCollapsible",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonIsCollapse"),
                        name: "isCollapse",
                        type: "boolean"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonIsShowRedirectIcon"),
                        type: "boolean | NCoreUIKitIcon",
                        name: "isShowRedirectIcon"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonProps"),
                        type: "object",
                        name: "props"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonBadge"),
                        name: "badge",
                        type: "number"
                    },
                    {
                        description: t("embeddedMenuPage.propButtonSubButtons"),
                        type: "Array<EmbeddedMenuButton>",
                        name: "subButtons"
                    }
                ]}
            />
            <Paragraph>
                {t("embeddedMenuPage.partButtonNestedText")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    props
                </InlineCode>
                {" "}
                {t("embeddedMenuPage.propButtonPropsText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("embeddedMenuPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default EmbeddedMenuPage;
